import { collection, doc, getDocs, query, Timestamp, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { deleteDoc, setDoc, updateDoc } from '../config/escrituras';
import { URL_PUBLICA } from '../config/marca';

/**
 * El enlace secreto para suscribirse al calendario de Tareas
 * (`calendariosTareas/{token}`, ver la función calendario-tareas). Uno por
 * persona: cambiarlo crea otro y borra el viejo, que deja de funcionar.
 */

export interface EnlaceCalendario {
  token: string;
  soloMias: boolean;
}

/** 32 caracteres aleatorios en base64url: imposible de adivinar. */
const nuevoToken = () => {
  const b = crypto.getRandomValues(new Uint8Array(24));
  return btoa(String.fromCharCode(...Array.from(b))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/** https://…/.netlify/functions/calendario-tareas?t=… */
export const urlCalendario = (token: string) =>
  `${URL_PUBLICA}/.netlify/functions/calendario-tareas?t=${encodeURIComponent(token)}`;

/** La misma con webcal://, que abre «Suscribirse» en el Calendario de iOS y en Outlook. */
export const urlWebcal = (token: string) => urlCalendario(token).replace(/^https?:\/\//, 'webcal://');

/** Google Calendar no abre webcal://: se le pasa por su página de «añadir por URL». */
export const urlGoogleCalendar = (token: string) =>
  `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(urlWebcal(token))}`;

export async function leerEnlaceCalendario(uid: string): Promise<EnlaceCalendario | null> {
  const snap = await getDocs(query(collection(db, 'calendariosTareas'), where('uid', '==', uid)));
  const d = snap.docs[0];
  return d ? { token: d.id, soloMias: d.get('soloMias') === true } : null;
}

export async function crearEnlaceCalendario(uid: string, soloMias: boolean): Promise<EnlaceCalendario> {
  const token = nuevoToken();
  await setDoc(doc(db, 'calendariosTareas', token), { uid, soloMias, createdAt: Timestamp.now() });
  return { token, soloMias };
}

export const quitarEnlaceCalendario = (token: string) => deleteDoc(doc(db, 'calendariosTareas', token));

export const cambiarSoloMias = (token: string, soloMias: boolean) =>
  updateDoc(doc(db, 'calendariosTareas', token), { soloMias });

/** Uno nuevo con los mismos ajustes; el viejo se borra y deja de servir. */
export async function renovarEnlaceCalendario(uid: string, viejo: EnlaceCalendario): Promise<EnlaceCalendario> {
  const nuevo = await crearEnlaceCalendario(uid, viejo.soloMias);
  await quitarEnlaceCalendario(viejo.token);
  return nuevo;
}
